import React, { useState, useEffect, useCallback, useRef } from 'react';
import dateformat from 'utils/dateformat';
import { Map as MapComponent, Filter } from './components';
import Reload from '../../components/Reload/Reload';
import ApiGServer from 'services/ApiGServer';
import ApiMServer from 'services/ApiMServer';
import Api from 'services/Api';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const MapsCalibration = () => {
  const user = JSON.parse(localStorage.getItem('userData'));

  if (user?.permission?.maps?.read !== true) {
    window.location.replace('/home');
  }

  const [zoom] = useState(5);
  const [allPoints, setAllPoints] = useState([]);
  const [points, setPoints] = useState([]);
  const [mapPoints, setMapPoints] = useState(false);
  const [center] = useState({ lat: -14.8227361, lng: -54.1549677 });
  const [companyNames, setCompanyNames] = useState([]);
  const [state, setState] = useState([])
  const [city, setCity] = useState([])
  const [companies, setCompanies] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [equipmentsRaw, setEquipmentsRaw] = useState([]);
  const [nearThreshold, setNearThreshold] = useState(60); // padrão 60 dias
  const [lastFilters, setLastFilters] = useState(null);
  const [lastSelectedState, setLastSelectedState] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    // Verifica se existe uma preferência salva no localStorage
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : false;
  });

  let [loading, setLoading] = useState(true);

  useEffect(() => {
    // Monitora mudanças de tema
    const handleThemeChange = () => {
      const isDark = document.body.classList.contains('dark-theme');
      setDarkMode(isDark);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Threshold legado de 30 dias (mantido visual anterior)
  const CALIBRATION_THRESHOLD_DAYS = 30;

  // Persist nearThreshold changes
  // Sem barra de ajuste; mantemos 60 fixo

  // Função util para calcular flags de calibração de um equipamento
  const computeCalibrationData = useCallback((equipment) => {
    let calibrationDate = null;
    if (equipment.dcal) {
      const base = new Date(equipment.dcal);
      if (!isNaN(base.getTime())) {
        const due = new Date(base);
        due.setFullYear(due.getFullYear() + 1);
        calibrationDate = due;
      }
    }
    if (!calibrationDate) {
      const calibrationRaw = equipment.calibrationDueDate || equipment.calibrationDate || (equipment.calibration && (equipment.calibration.dueDate || equipment.calibration.expirationDate)) || equipment.calibracaoValidade;
      calibrationDate = calibrationRaw ? new Date(calibrationRaw) : null;
    }
    let daysToExpire = null;
    let nearCalibrationLegacy = false; // <=30 dias
    let nearCalibration90 = false;     // <=90 dias (fixo)
    let nearCalibrationDynamic = false; // <= nearThreshold (dinâmico)
    let expiredCalibration = false;
    if (calibrationDate && !isNaN(calibrationDate.getTime())) {
      const diffMs = calibrationDate.getTime() - Date.now();
      daysToExpire = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      if (daysToExpire <= CALIBRATION_THRESHOLD_DAYS) nearCalibrationLegacy = true;
      if (daysToExpire >= 0 && daysToExpire <= 90) nearCalibration90 = true;
      if (daysToExpire >= 0 && daysToExpire <= nearThreshold) nearCalibrationDynamic = true;
      if (daysToExpire < 0) {
        expiredCalibration = true;
        nearCalibrationLegacy = false;
        nearCalibration90 = false;
        nearCalibrationDynamic = false;
      }
    }
    return { calibrationDate, daysToExpire, nearCalibrationLegacy, nearCalibration90, nearCalibrationDynamic, expiredCalibration };
  }, [nearThreshold]);

  // Reprocessa todos os equipamentos para atualizar pontos e estados
  const recomputeData = useCallback((equipments, locatedList, connected, updateView = true) => {
    if (!equipments || equipments.length === 0) return;
    const result = [];
    equipments.forEach(equipment => {
      let lat = Number(parseFloat(equipment.lat).toFixed(7));
      let lng = Number(parseFloat(equipment.lng).toFixed(7));
      let date = dateformat(equipment.updatedAt);
      let serial = equipment.serialnumber;
      let versaosw = equipment.versaosw;
      let versaofwpri = equipment.versaofwpri;
      let versaofwosc = equipment.versaofwosc;
      let located = locatedList.includes(serial);
      let isConnected = connected.includes(serial);
      let address = equipment.address || '';
      const calib = computeCalibrationData(equipment);
      if (lat === 0 || lng === 0) return;
      if (!isNaN(lat) && !isNaN(lng)) {
        // Inclusão de daystoactivate para permitir filtro de bloqueados (0)
        result.push({ serial, lat, lng, date, versaosw, versaofwpri, versaofwosc, located, isConnected, address, dcal: equipment.dcal || null, daystoactivate: equipment.daystoactivate, ...calib });
      }
    });
    let pts = result.map(item => ({
      located: item.located,
      type: 'Feature',
      properties: { cluster: false, id: item.serial, data: item },
      geometry: { type: 'Point', coordinates: [parseFloat(item.lng), parseFloat(item.lat)] },
      address: item.address
    }));
    pts = pts.sort((a, b) => {
      if (a.properties.data.serial > b.properties.data.serial) return 1; // fix typo stitleerial
      if (a.properties.data.serial < b.properties.data.serial) return -1;
      return 0;
    });
    setPoints(pts);
    if (updateView) {
      setAllPoints(pts);
    }

    // Monta estados enriquecidos (usa nearCalibrationDynamic)
    const stateStats = new Map();
    const getStateInfo = (s) => {
      if (!s) return { value: '', uf: '', name: '', labelBase: '' };
      if (typeof s === 'string') return { value: s, uf: s, name: '', labelBase: s };
      const uf = s.uf || s.sigla || s.code || s.UF || s.shortname || '';
      const name = s.name || s.nome || s.estado || s.label || s.longname || '';
      const value = uf || name;
      const labelBase = uf && name ? `${uf} - ${name}` : (uf || name);
      return { value, uf, name, labelBase };
    };
    equipments.forEach(eq => {
      const s = eq?.address?.state;
      const { value, labelBase } = getStateInfo(s);
      if (!value) return;
      const calib = computeCalibrationData(eq);
      if (!stateStats.has(value)) stateStats.set(value, { value, labelBase, total: 0, expired: 0, nearDynamic: 0 });
      const stat = stateStats.get(value);
      stat.total += 1;
      if (calib.expiredCalibration) stat.expired += 1;
      else if (calib.nearCalibrationDynamic) stat.nearDynamic += 1;
    });
    const options = Array.from(stateStats.values()).map(stat => {
      const suffixParts = [];
      const equipWord = stat.total === 1 ? 'equipamento' : 'equipamentos';
      suffixParts.push(`${stat.total} ${equipWord}`);
      if (stat.expired > 0) suffixParts.push(`exp: ${stat.expired}`);
      if (stat.nearDynamic > 0) suffixParts.push(`em breve: ${stat.nearDynamic}`);
      const label = `${stat.labelBase} — ${suffixParts.join(' · ')}`;
      return { value: stat.value, label };
    }).sort((a, b) => a.label.localeCompare(b.label));
    setStatesOptions(options);
  }, [computeCalibrationData]);

  useEffect(() => {
    (async () => {
      const { data: equipments } = await ApiGServer.get('/equipment/all/filtered');
      const locatedList = [].concat.apply([], equipments.filter(e => e.mode === 'RENTAL').map(i => i.serialnumber));
      const response1 = await Api.get('/connection/status');
      const connectedEquips = response1.data;
      const connected = connectedEquips.map(item => item.serialnumber);
      setEquipmentsRaw(equipments);
      recomputeData(equipments, locatedList, connected);
      setLoading(false);

      const results = [];
      const pinss = equipments.filter(i => i.address && i.address.state)
        .map(async (equipment) => {
          let lat = Number(parseFloat(equipment.lat).toFixed(8));
          let lng = Number(parseFloat(equipment.lng).toFixed(8));
          let date = dateformat(equipment.updatedAt);
          let serial = equipment.serialnumber;
          let versaosw = equipment.versaosw;
          let versaofwpri = equipment.versaofwpri;
          let versaofwosc = equipment.versaofwosc;
          let located = locatedList.includes(serial);

          let calibrationDate = null;
          if (equipment.dcal) {
            const base = new Date(equipment.dcal);
            if (!isNaN(base.getTime())) {
              const due = new Date(base);
              due.setFullYear(due.getFullYear() + 1);
              calibrationDate = due;
            }
          }
          if (!calibrationDate) {
            const calibrationRaw = equipment.calibrationDueDate || equipment.calibrationDate || (equipment.calibration && (equipment.calibration.dueDate || equipment.calibration.expirationDate)) || equipment.calibracaoValidade;
            calibrationDate = calibrationRaw ? new Date(calibrationRaw) : null;
          }
          let daysToExpire = null;
          let nearCalibration = false;
          let nearCalibration90 = false;
          let expiredCalibration = false;
          if (calibrationDate && !isNaN(calibrationDate.getTime())) {
            const diffMs = calibrationDate.getTime() - Date.now();
            daysToExpire = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (daysToExpire <= CALIBRATION_THRESHOLD_DAYS) nearCalibration = true;
            if (daysToExpire >= 0 && daysToExpire <= 90) nearCalibration90 = true;
            if (daysToExpire < 0) { expiredCalibration = true; nearCalibration = false; }
          }

          if (equipment.address !== "" && equipment.address.state !== null) {
            lat = Number(parseFloat(equipment.address.state.lat).toFixed(8));
            lng = Number(parseFloat(equipment.address.state.lng).toFixed(8));
          }
          if (lat === 0 || lng === 0) { return }
          if (!isNaN(lat) && !isNaN(lng))
            results.push({ serial, lat, lng, date, versaosw, versaofwpri, versaofwosc, located, dcal: equipment.dcal || null, daystoactivate: equipment.daystoactivate, calibrationDate, daysToExpire, nearCalibration, nearCalibration90, expiredCalibration });
        });
      await Promise.all(pinss);

      let statePoints = results.map(item => ({
        located: item.located,
        type: "Feature",
        properties: {
          cluster: false,
          id: item.serial,
          data: item
        },
        geometry: {
          type: "Point",
          coordinates: [
            parseFloat(item.lng),
            parseFloat(item.lat)
          ]
        },
        address: item.address
      }));

      statePoints = statePoints.sort(function (a, b) {
        if (a.properties.data.serial > b.properties.data.serial) {
          return 1;
        }
        if (a.properties.data.serial < b.properties.data.serial) {
          return -1;
        }
        return 0;
      });
      setState(statePoints);

      const resultss = [];
      const pinsss = equipments.filter(i => i.address && i.address.city)
        .map(async (equipment) => {
          let lat = Number(parseFloat(equipment.lat).toFixed(7));
          let lng = Number(parseFloat(equipment.lng).toFixed(7));
          let date = dateformat(equipment.updatedAt);
          let serial = equipment.serialnumber;
          let versaosw = equipment.versaosw;
          let versaofwpri = equipment.versaofwpri;
          let versaofwosc = equipment.versaofwosc;
          let located = locatedList.includes(serial);

          let calibrationDate = null;
          if (equipment.dcal) {
            const base = new Date(equipment.dcal);
            if (!isNaN(base.getTime())) {
              const due = new Date(base);
              due.setFullYear(due.getFullYear() + 1);
              calibrationDate = due;
            }
          }
          if (!calibrationDate) {
            const calibrationRaw = equipment.calibrationDueDate || equipment.calibrationDate || (equipment.calibration && (equipment.calibration.dueDate || equipment.calibration.expirationDate)) || equipment.calibracaoValidade;
            calibrationDate = calibrationRaw ? new Date(calibrationRaw) : null;
          }
          let daysToExpire = null;
          let nearCalibration = false;
          let nearCalibration90 = false;
          let expiredCalibration = false;
          if (calibrationDate && !isNaN(calibrationDate.getTime())) {
            const diffMs = calibrationDate.getTime() - Date.now();
            daysToExpire = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (daysToExpire <= CALIBRATION_THRESHOLD_DAYS) nearCalibration = true;
            if (daysToExpire >= 0 && daysToExpire <= 90) nearCalibration90 = true;
            if (daysToExpire < 0) { expiredCalibration = true; nearCalibration = false; }
          }

          if (equipment.address !== "" && equipment.address.city !== null) {
            lat = Number(parseFloat(equipment.address.city.lat).toFixed(7));
            lng = Number(parseFloat(equipment.address.city.lng).toFixed(7));
          }
          if (lat === 0 || lng === 0) { return }
          if (!isNaN(lat) && !isNaN(lng))
            resultss.push({ serial, lat, lng, date, versaosw, versaofwpri, versaofwosc, located, dcal: equipment.dcal || null, daystoactivate: equipment.daystoactivate, calibrationDate, daysToExpire, nearCalibration, nearCalibration90, expiredCalibration });
        });
      await Promise.all(pinsss);

      let cityPoints = resultss.map(item => ({
        located: item.located,
        type: "Feature",
        properties: {
          cluster: false,
          id: item.serial,
          data: item
        },
        geometry: {
          type: "Point",
          coordinates: [
            parseFloat(item.lng),
            parseFloat(item.lat)
          ]
        },
        address: item.address
      }));

      cityPoints = cityPoints.sort(function (a, b) {
        if (a.properties.data.serial > b.properties.data.serial) {
          return 1;
        }
        if (a.properties.data.serial < b.properties.data.serial) {
          return -1;
        }
        return 0;
      });

      setCity(cityPoints);
    })();
  }, [recomputeData]);

  // Recalcula dados ao mudar nearThreshold (evita loop reapplying)
  const reapplyingRef = useRef(false);
  useEffect(() => {
    if (equipmentsRaw.length === 0) return;
    const locatedList = [].concat.apply([], equipmentsRaw.filter(e => e.mode === 'RENTAL').map(i => i.serialnumber));
    const connectedSet = new Set(points.filter(p => p.properties.data.isConnected).map(p => p.properties.data.serial));
    const connected = Array.from(connectedSet);
    const shouldUpdateView = !lastFilters;
    recomputeData(equipmentsRaw, locatedList, connected, shouldUpdateView);
    if (lastFilters) {
      reapplyingRef.current = true;
      setTimeout(() => {
        filter(lastFilters, '', '', lastSelectedState);
        reapplyingRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearThreshold, equipmentsRaw, recomputeData]);


  useEffect(() => {
    (async () => {
      const { data } = await ApiMServer.get('/company/v1/getcooperative');
      setCompanies(data);
      let filterCompany = data.filter((item) => {
        return item.typeCompany === true;
      })
      let cNames = [].concat.apply([], filterCompany.map((company) => {
        return company.name;
      }));
      setCompanyNames(cNames.sort());
    })();
  }, []);

  const filter = (filters, company, serial, selectedState) => {
    if (!reapplyingRef.current) {
      setLastFilters(Array.isArray(filters) ? [...filters] : null);
      setLastSelectedState(selectedState || '');
    }
    // Determina a fonte base (cidades ou normal)
    let sourcePoints;
    if (filters[8]) { // CIDADES
      sourcePoints = [...city];
      setMapPoints(true);
    } else {
      sourcePoints = [...points];
      setMapPoints(false);
    }

    // Composição de filtros: começa com base e aplica interseções
    let result = [...sourcePoints];

    // Se um estado foi selecionado, filtra por estado (aplicável no modo normal)
    if (!filters[8] && selectedState) {
      const belongsToState = (item, ufOrName) => {
        const s = item?.address?.state;
        if (!s) return false;
        if (!ufOrName) return false;
        const target = String(ufOrName).toLowerCase();
        if (typeof s === 'string') {
          return String(s).toLowerCase() === target;
        }
        const uf = (s.uf || s.sigla || s.code || s.UF || s.shortname || '').toLowerCase();
        const name = (s.name || s.nome || s.estado || s.label || s.longname || '').toLowerCase();
        return uf === target || name === target;
      };
      result = result.filter(p => belongsToState(p, selectedState));
    }

    // Locação / Não Locação (interseção se ativos)
    if (filters[1]) {
      result = result.filter(item => !!item.located === true);
    }
    if (filters[14]) {
      result = result.filter(item => !!item.located === false);
    }

    // Bloqueados (daystoactivate 0 ou '0') - funciona como restrição base (interseção)
    if (filters[15]) {
      result = result.filter(item => item?.properties?.data?.daystoactivate === 0 || item?.properties?.data?.daystoactivate === '0');
    }

    // Tipos (SIMT, BIMT, CPMT, EEMT, ESMR, 999DRY) — OR dentro do grupo, AND com demais
    const typePredicates = [
      { idx: 2, fn: i => i?.properties?.data?.serial?.includes('SIMT') },
      { idx: 3, fn: i => i?.properties?.data?.serial?.includes('BIMT') },
      { idx: 4, fn: i => i?.properties?.data?.serial?.includes('CPMT') },
      { idx: 5, fn: i => i?.properties?.data?.serial?.includes('EEMT') },
      { idx: 6, fn: i => i?.properties?.data?.serial?.includes('ESMR') },
      { idx: 10, fn: i => i?.properties?.data?.serial?.includes('SEMT') }
    ];
    const activeTypeFns = typePredicates.filter(tp => filters[tp.idx]).map(tp => tp.fn);
    if (activeTypeFns.length > 0) {
      result = result.filter(i => activeTypeFns.some(fn => fn(i)));
    }

    // Calibração — OR dentro do grupo, AND com demais
    const calPredicates = [];
    if (filters[11]) calPredicates.push(i => i?.properties?.data?.nearCalibration || i?.properties?.data?.expiredCalibration);
    if (filters[12]) calPredicates.push(i => i?.properties?.data?.expiredCalibration);
    if (filters[13]) calPredicates.push(i => i?.properties?.data?.nearCalibrationDynamic);
    if (calPredicates.length > 0) {
      result = result.filter(i => calPredicates.some(fn => fn(i)));
    }

    // ONLINE, SERIAL, EMPRESA
    result = applyAdditionalFilters(result, filters[9], serial, company);

    setAllPoints(result);
  };

  // Função auxiliar para aplicar filtros adicionais
  const applyAdditionalFilters = (points, isOnline, serial, company) => {
    let result = [...points];

    // Filtro de equipamentos online
    if (isOnline) {
      result = result.filter(item => item?.properties?.data?.isConnected);
    }

    // Filtro por número de série
    if (serial) {
      console.log(serial);

      result = result.filter(item => item?.properties?.data?.serial?.toUpperCase().includes(serial?.toUpperCase()));
    }

    // Filtro por empresa
    if (company && company !== '') {
      const companyEquipments = companies
        .find(i => i.name === company)?.equipments || [];
      result = result.filter(item =>
        companyEquipments.includes(item?.properties?.data?.serial)
      );
    }

    return result;
  };

  return (
    <div
      className="map-container"
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: 'var(--map-background)'
      }}
    >
      {loading && <Reload />}

      {/* Ação de exportar CSV baseada no filtro atual */}
      { /* Implementado como função local e passada para Filter */ }
      

      
      <Filter
        filter={filter}
        count={allPoints.length}
        darkMode={darkMode}
        statesOptions={statesOptions}
        nearThreshold={nearThreshold}
        onThresholdChange={setNearThreshold}
        onExport={() => {
          try {
            const headers = [
              'serial', 'lat', 'lng', 'updatedAt', 'located', 'isConnected', 'address', 'state', 'city',
              'dcal', 'calibrationDate', 'daysToExpire', 'nearCalibrationLegacy', 'nearCalibration90', 'nearCalibrationDynamic', 'expiredCalibration', 'daystoactivate'
            ];

            const csvEscape = (val) => {
              const s = val === null || val === undefined ? '' : String(val);
              const escaped = s.replace(/"/g, '""');
              return `"${escaped}"`;
            };

            const rows = allPoints.map((p) => {
              const d = p?.properties?.data || {};
              const addr = d.address || p.address || {};
              const state = typeof addr?.state === 'string' ? addr.state
                : (addr?.state?.uf || addr?.state?.sigla || addr?.state?.code || addr?.state?.shortname || addr?.state?.name || '')
              const city = typeof addr?.city === 'string' ? addr.city
                : (addr?.city?.name || addr?.city?.nome || '')
              return [
                d.serial,
                d.lat,
                d.lng,
                d.date,
                d.located,
                d.isConnected,
                typeof addr === 'string' ? addr : (addr?.formatted || ''),
                state,
                city,
                d.dcal || '',
                d.calibrationDate ? new Date(d.calibrationDate).toISOString() : '',
                d.daysToExpire ?? '',
                d.nearCalibrationLegacy ?? '',
                d.nearCalibration90 ?? '',
                d.nearCalibrationDynamic ?? '',
                d.expiredCalibration ?? '',
                d.daystoactivate ?? ''
              ];
            });

            const headerLine = headers.map(csvEscape).join(',');
            const dataLines = rows.map(r => r.map(csvEscape).join(',')).join('\n');
            const content = `${headerLine}\n${dataLines}`;
            const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const ts = new Date().toISOString().replace(/[:.]/g, '-');
            a.href = url;
            a.download = `relatorio-calibracao-${ts}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } catch (e) {
            console.error('Falha ao gerar CSV', e);
          }
        }}
      />

      {points.length > 0 && (
        <MapComponent
          zoom={zoom}
          center={center}
          containerElement={<div style={{ height: '100%' }} />}
          googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${GOOGLE_API_KEY}&language=pt-br`}
          loadingElement={<div style={{ height: '100%' }} />}
          mapElement={<div style={{ height: '100%' }} />}
          mapPoints={mapPoints}
          points={allPoints}
          darkMode={darkMode}
          useUserLocation={true}
        />
      )}
    </div>
  );
};
export default MapsCalibration;
